import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "shepherd.js/dist/css/shepherd.css";
import { AnimatePresence } from "motion/react";
import { Campaign, Organization, Review } from "../types";
import { datasource } from "../datasource/datasource";
import { useNavigate } from "react-router";
import { useAuth, useUser as useClerkUser } from "@clerk/clerk-react";
import "./dashboard.css";
import { SideBar, SideBarItemData } from "../components/sidebar";
import useScreenDimensions from "../hooks/useScreenDimensions";
import { DashBoardTopBar } from "../components/dashboardTopBar";
import { Drawer, Snackbar, SwipeableDrawer } from "@mui/material";
import { DashboardContentSection } from "../components/dashboardContentSection";
import { Loading } from "../recorderComponents/loading";
import {
  CampaignFrontend,
  campaignStub,
} from "../syncedTypes";
import useCampaigns from "../hooks/useCampaigns";
import useOrganziations from "../hooks/useOrganizations";
import useUser from "../hooks/useUser";
import {
  ContextMenu,
  ContextMenuContext,
  Cords,
} from "../components/ContextMenu";
import { CampaingModal } from "../components/CampaignModal";
import { DeleteCampaignModal } from "../components/DeleteCampaignModal";
import { StandardButton } from "../components/standardButton";
import * as motion from "motion/react-client";
import posthog from "posthog-js";
import { CannotCreateNewCampaignModal } from "../components/CannotCreateNewCampaignModal";
import { QRCodeModal } from "../components/qrCodeModal";
import { isMobile } from "react-device-detect";
import HamburgerMenuIcon from "../components/HamburgerMenuIcon";
import { LinkModal } from "../components/linkModal";
import {
  DontHaveASubModal,
  useDontHaveASubModal,
} from "../components/DontHaveASubModal";
import { ShepherdTourContext, shepherdTourInstance } from "../lib/shepherd";
import { useShepherdGlobalState } from "../hooks/useShepherdGlobalState";


export const DashboardContext = createContext<{
  updateReviews: (reviews: Review[]) => void;
  openSnackBar: () => void;
  isExpanded: boolean;
  setIsExpanded: (newVal: boolean) => void;
}>({
  updateReviews: (reviews: Review[]) => undefined,
  openSnackBar: () => undefined,
  isExpanded: false,
  setIsExpanded: (newVal: boolean) => undefined,
});

export const VideoPlayerContext = createContext<{
  videoPlayingHash: string;
  updatePlayingHash: (hash: string) => void;
}>({
  updatePlayingHash: (hash: string) => undefined,
  videoPlayingHash: "",
});

export const CampaignModalContext = createContext<{
  isOpen: boolean;
  // setIsOpen: (newVal: boolean) => void;
  campaignToEdit: CampaignFrontend | null;
  setCampaignToEdit: (campaign: CampaignFrontend | null) => void;
  setOpenNewCampaign: () => void;
  setOpenEditCampaign: (campaignToEdit: CampaignFrontend) => void;
  closeModal: () => void;
}>({
  isOpen: false,
  // setIsOpen: (newVal: boolean) => undefined,
  campaignToEdit: null,
  setCampaignToEdit: (campaign: CampaignFrontend | null) => undefined,
  setOpenNewCampaign: () => undefined,
  setOpenEditCampaign: (campaignToEdit: CampaignFrontend) => undefined,
  closeModal: () => undefined,
});

export const DeleteCampaignModalContext = createContext<{
  isOpen: boolean;
  setIsOpen: (newVal: boolean) => void;
  campaignId: number;
  setCampaignId: (num: number) => void;
}>({
  isOpen: false,
  setIsOpen: (newVal: boolean) => undefined,
  campaignId: 0,
  setCampaignId: (num: number) => undefined,
});

export const Dashboard = () => {
  const nav = useNavigate();
  const { getToken, userId, isLoaded } = useAuth();
  const { isSignedIn } = useClerkUser();

  const currentCampaigns = useCampaigns((state) => state.allCampaigns);
  const activeCampaign = useCampaigns((state) => state.selectedCampaign);
  const setCurrentCampaigns = useCampaigns((state) => state.setCampaigns);
  const setActiveCampaign = useCampaigns((state) => state.setSelectedCampaign);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [deleteCampaginModalIsOpen, setDeleteCampaginModalIsOpen] =
    useState<boolean>(false);
  const [campaignIdToDelete, setCampaignIdToDelete] = useState<number>(0);
  const [campaignModalIsOpen, setCampaignModalIsOpen] =
    useState<boolean>(false);
  const [campaignToEdit, setCampaignToEdit] = useState<CampaignFrontend | null>(
    null
  );
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const idOfLastCampaignRef = useRef<number | null>(null);

  const setOpenNewCampaign = useCallback(() => {
    setCampaignToEdit(null);
    setCampaignModalIsOpen(true);
  }, [setCampaignToEdit, setCampaignModalIsOpen]);

  const setOpenEditCampaign = useCallback(
    (campaign: CampaignFrontend) => {
      setCampaignToEdit(campaign);
      setCampaignModalIsOpen(true);
    },
    [setCampaignToEdit, setCampaignModalIsOpen]
  );

  const closeModal = useCallback(() => {
    setCampaignModalIsOpen(false);
  }, [setCampaignModalIsOpen, setCampaignToEdit]);

  const setUserOrganizations = useOrganziations(
    (state) => state.setAllOrganizations
  );

  useEffect(() => {
    if (idOfLastCampaignRef.current !== activeCampaign?.id) {
      setIsExpanded(false);
      idOfLastCampaignRef.current = activeCampaign?.id || null;
    }
  }, [activeCampaign]);

  const updateCampaign = useCampaigns((state) => state.updateCampaign);

  const activeOrg = useOrganziations((state) => state.activeOrganization);

  const timeoutRef = useRef<number | NodeJS.Timeout>(0);

  useEffect(() => {
    const pollAndSetTimeout = async () => {
      clearTimeout(timeoutRef.current);
      if (activeOrg?.id && currentCampaigns.length) {
        const hashMap: { [key: number]: string | null } = {};
        currentCampaigns.forEach((el) => {
          hashMap[el.id] = el.reviews.length ? el.reviews[0].hash : null;
        });
        const data = await datasource.poll(
          activeOrg.id,
          activeOrg.planTypeId,
          currentCampaigns.map((el) => el.id),
          hashMap
        );
        if (data.campaignsThatNeedToBeUpdated.length) {
          const token = await getToken();
          data.campaignsThatNeedToBeUpdated.forEach(async (el: number) => {
            const updatedCampaign = await datasource.getCampaignInfo(
              el,
              token as string
            );
            updateCampaign(updatedCampaign);
          });
        }
      }
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(
        pollAndSetTimeout,
        5000 + Math.ceil(Math.random() * 100)
      );
    };
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(
      pollAndSetTimeout,
      5000 + Math.ceil(Math.random() * 100)
    );

    return () => clearTimeout(timeoutRef.current);
  }, [activeOrg, currentCampaigns]);

  const setDontHaveSubModal = useDontHaveASubModal((state) => state.setIsOpen);

  useEffect(() => {
    if (activeOrg?.id && activeOrg.planTypeId === 1) {
      setDontHaveSubModal(true);
    }
  }, [activeOrg]);

  const setActiveOrg = useOrganziations((state) => state.setActiveOrganization);
  const [isLoading, setIsLoading] = useState<boolean>(!currentCampaigns.length);
  const [firstLoadStubs, setFirstLoadStubs] = useState<campaignStub[]>([]);

  const [contextMenuIsOpen, setContextMenuIsOpen] = useState<boolean>(false);
  const [contextMenuOptions, setContextMenuOptions] = useState<JSX.Element[]>(
    []
  );
  const [contextMenuCords, setContextMenuCords] = useState<Cords>({
    x: 0,
    y: 0,
  });
  const closeContextMenu = useCallback(() => {
    setContextMenuIsOpen(false);
    setContextMenuOptions([]);
    setContextMenuCords({ x: 0, y: 0 });
  }, [setContextMenuIsOpen, setContextMenuOptions, setContextMenuCords]);
  const openContextMenu = useCallback(
    (cords: Cords, options: JSX.Element[]) => {
      setContextMenuCords(cords);
      setContextMenuOptions(options);
      setContextMenuIsOpen(true);
    },
    [setContextMenuIsOpen, setContextMenuOptions, setContextMenuCords]
  );

  const [videoPlayingHash, setVideoPlayingHash] = useState<string>("");

  const setUser = useUser((state) => state.setUser);

  useEffect(() => {
    if (isLoaded && userId) {
      nav("/auth/dashboard");
    } else if (isLoaded && !userId) {
      nav("/auth/set-up");
    }
  }, [userId, isLoaded]);

  const openSnackBar = useCallback(() => {
    setSnackBarOpen(true);
    setTimeout(() => setSnackBarOpen(false), 3000);
  }, [setSnackBarOpen]);

  const displayedReviews = useMemo(() => {
    return activeCampaign?.reviews ?? [];
  }, [activeCampaign]);

  const { height, width } = useScreenDimensions();

  useEffect(() => {
    // let i: any;
    if (currentCampaigns.length) {
      return;
    } else {
      setIsLoading(true);
      const setup = async () => {
        const token = await getToken();
        const userOrgs = await datasource.getUserOrgs(token as string);
        if (!userOrgs.length) {
          nav("/auth/set-up");
          return;
        }
        datasource.getUser(token as string).then((user) => {
          setUser(user);
          posthog.identify(user.email);
        });
        const initialData = await datasource.getInitialData(
          userOrgs[0].id,
          token as string
        );
        const campaignPromMap = initialData.campaignStubs.map((el) =>
          datasource.getCampaignInfo(el.id, token as string)
        );

        setUserOrganizations(userOrgs);
        setActiveOrg(userOrgs[0]);
        setCurrentCampaigns([initialData.firstCampaign]);
        setActiveCampaign(initialData.firstCampaign);
        setFirstLoadStubs(initialData.campaignStubs);
        setIsLoading(false);

        const allCampaigns = await Promise.all(campaignPromMap);
        setCurrentCampaigns(allCampaigns);
      };
      setup();
      // i = setInterval(setup, 5000)
    }
    return () => {
      posthog.reset();
      // @ts-ignore
      // clearInterval(i);
    };
  }, []);

  useEffect(() => {
    if (!isSignedIn && isLoaded) {
      nav("/signup");
    }
  }, [isSignedIn, isLoaded]);

  const sideBarItems = useMemo(() => {
    return currentCampaigns.map((el) => {
      return {
        ...el,
        isSelected: el.id === activeCampaign?.id,
      };
    });
  }, [currentCampaigns, activeCampaign]);

  const setActiveSidebarItem = useCallback(
    (sidebarItem: SideBarItemData) => {
      const [selectedItem] = currentCampaigns.filter(
        (el) => el.id === sidebarItem.id
      );
      setActiveCampaign(selectedItem);
    },
    [setActiveCampaign, currentCampaigns]
  );

  const updateReviews = useCallback((reviews: Review[]) => {
    // setReviews(reviews)
  }, []);

  const handleCopy = useCallback(async () => {
    try {
      if (!activeCampaign) return;
      await navigator.clipboard.writeText(
        `${window.location.host}/c/${activeCampaign.hash}`
      );
      closeModal();
      openSnackBar();
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, [activeCampaign]);

  useEffect(() => {
    if (contextMenuIsOpen && isMobile) {
      setSidebarOpen(false);
      const widthOfGap = width * 0.15;
      setContextMenuCords({ x: widthOfGap / 2, y: height / 5 });
    }
  }, [contextMenuIsOpen]);

  const shouldOpenTour = useShepherdGlobalState(state=> state.shouldOpenTour);
  const setShouldOpenTour = useShepherdGlobalState(state => state.setShouldOpenTour);

  useEffect(() => {
    if(!isLoading && shouldOpenTour) {
      setTimeout(shepherdTourInstance.start, 1000)
      setShouldOpenTour(false);
      closeSidebar()
    }
  }, [isLoading, shouldOpenTour, closeSidebar])

  return (
    <ContextMenuContext.Provider
      value={{
        setOpen: openContextMenu,
        isOpen: contextMenuIsOpen,
        closeMenu: closeContextMenu,
        options: contextMenuOptions,
        cords: contextMenuCords,
      }}
    >
      <DashboardContext.Provider
        value={{ updateReviews, openSnackBar, isExpanded, setIsExpanded }}
      >
        <VideoPlayerContext.Provider
          value={{ videoPlayingHash, updatePlayingHash: setVideoPlayingHash }}
        >
          <CampaignModalContext.Provider
            value={{
              isOpen: campaignModalIsOpen,
              campaignToEdit,
              setCampaignToEdit,
              setOpenNewCampaign,
              setOpenEditCampaign,
              closeModal,
            }}
          >
            <DeleteCampaignModalContext.Provider
              value={{
                isOpen: deleteCampaginModalIsOpen,
                setIsOpen: setDeleteCampaginModalIsOpen,
                campaignId: campaignIdToDelete,
                setCampaignId: setCampaignIdToDelete,
              }}
            >
              <ShepherdTourContext.Provider value={shepherdTourInstance}>
                <Drawer
                  anchor="bottom"
                  open={isLoading}
                  PaperProps={{ sx: { backgroundColor: "#FFF8F0" } }}
                  transitionDuration={{ exit: 500, enter: 500 }}
                >
                  <Loading showLoading />
                </Drawer>
                <div
                  style={{
                    minHeight: height,
                    width: isMobile ? "100vw" : "100%",
                    display: isMobile ? "flex" : "flex",
                  }}
                >
                  <DontHaveASubModal />
                  <LinkModal />
                  <QRCodeModal />
                  <CannotCreateNewCampaignModal />
                  <DeleteCampaignModal />
                  <CampaingModal />
                  <ContextMenu />
                  <HamburgerMenuIcon
                    sidebarOpen={sidebarOpen}
                    onPress={openSidebar}
                  />
                  {isMobile ? (
                    <SwipeableDrawer
                      onClose={closeSidebar}
                      onOpen={openSidebar}
                      anchor="left"
                      open={sidebarOpen}
                      PaperProps={{ sx: { backgroundColor: "#FFF8F0" } }}
                      transitionDuration={{ exit: 500, enter: 500 }}
                    >
                      <SideBar />
                    </SwipeableDrawer>
                  ) : (
                    <SideBar />
                  )}
                  <div
                    className="mainPanelContainer"
                    style={{
                      marginLeft: isMobile ? 0 : "1rem",
                      backgroundColor: "#FFFFFF",
                      marginTop: isMobile ? 72 : "1rem",
                      borderRadius: 16,
                      marginBottom: "2rem",
                      width: isMobile ? "100%" : undefined,
                    }}
                  >
                    <DashBoardTopBar />
                    <DashboardContentSection reviews={displayedReviews} />
                  </div>
                  <Snackbar open={snackBarOpen} message="Copied to Clipboard" />
                  <AnimatePresence>
                    {isExpanded ? null : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{
                          position: "fixed",
                          right: isMobile ? "2vw" : "3rem",
                          bottom: isMobile ? "2vw" : "3rem",
                        }}
                      >
                        <StandardButton
                          shepardClass="copyLinkButton"
                          buttonText={
                            isMobile ? "Copy Link" : "Copy Link To Campaign"
                          }
                          onClick={handleCopy}
                          buttonColor="black"
                          customStyles={{
                            width: isMobile ? "9rem" : "15rem",
                            minWidth: isMobile ? "9rem" : "15rem",
                            fontSize: "1.1rem",
                            boxShadow: "0 6px 8px rgba(0,0,0,0.1",
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </ShepherdTourContext.Provider>
            </DeleteCampaignModalContext.Provider>
          </CampaignModalContext.Provider>
        </VideoPlayerContext.Provider>
      </DashboardContext.Provider>
    </ContextMenuContext.Provider>
  );
};
